import { useState } from "react";
import { stableRotation, ROTATION_MAX, formatDay } from "@oshimap/utils";
import { PASSPORT_SECTIONS, ROUTES } from "@oshimap/config";
import type { PassportSection } from "@oshimap/config";
import { navigate } from "../../app/router";
import { Button, Chip, EmptyState, ErrorState, Skeleton } from "../../components/ui";
import { GuestState } from "../../components/GuestState";
import { session } from "../../lib/api";
import { usePassport } from "./usePassport";
import { WorldView } from "./WorldView";
import { ShareSheet } from "./ShareSheet";
import { flip } from "../../lib/motion";
import "./passport.css";
import { useAuthPrompt } from "../auth/useAuthPrompt";
import { AuthSheet } from "../auth/AuthSheet";

const TIMELINE_MAX = 20;

const TAB_LABEL: Record<PassportSection, string> = { stamps: "스탬프 북", regions: "지역", world: "세계" };

/**
 * 여권은 제품 정체성이다 (00 §5 Layer 3).
 * Primary UX 는 Stamp Book + Region List. World View 는 세 번째 탭이며 Bottom Nav 에 없다 (W1).
 * OD-007 이 승인한 `/passport/world` 는 탭이 URL 을 바꿔야 실제로 도달할 수 있는 주소가 된다.
 */
export function PassportScreen({ section }: { section?: string }) {
  const auth = useAuthPrompt();
  const [sharing, setSharing] = useState(false);
  const [flipped, setFlipped] = useState<string | null>(null);
  const pane: PassportSection =
    PASSPORT_SECTIONS.includes(section as PassportSection) ? (section as PassportSection) : "stamps";
  const { state, derived, refresh } = usePassport();

  if (session.state.status !== "authenticated") {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="om-h1">덕질 여권</h1>
        <GuestState signedOut={{ title: "로그인하면 여권이 열립니다", body: "다녀온 곳이 스탬프로 남고, 지역과 최애별로 모입니다." }} auth={auth} />
        {auth.reason ? <AuthSheet reason={auth.reason} onClose={auth.close} /> : null}
      </div>
    );
  }
  /*
   * 로딩·오류 화면에도 h1 이 있어야 합니다. 없으면 화면을 못 보는 사람은
   * **여기가 어디인지 모른 채** 오류 문장부터 듣습니다. 그리고 문서가 h2 로
   * 시작해 헤딩 단계가 건너뛰어집니다.
   *
   * 게스트 훑기로는 안 보였습니다 — 게스트는 로그인 안내 가지로 가고, 정상
   * 응답이면 본문 가지로 갑니다. 이 두 가지만 h1 이 없었습니다.
   */
  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="om-h1">덕질 여권</h1>
        <Skeleton lines={3} />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="om-h1">덕질 여권</h1>
        <ErrorState title="여권을 불러오지 못했어요" body={state.error.message} onRetry={refresh} />
      </div>
    );
  }
  /* 여기까지 오면 응답은 성공인데 파생값이 없다는 뜻입니다 — 빈 화면으로 끝내지 않습니다. */
  if (!derived) {
    return (
      <div className="om-screen om-screen--pad">
        <h1 className="om-h1">덕질 여권</h1>
        <ErrorState title="여권을 읽지 못했어요" body="응답 모양이 예상과 달라요." onRetry={refresh} />
      </div>
    );
  }

  const { passport: p, heldStamps, isEmpty } = derived;

  return (
    <div className="om-screen om-passport">
      <h1 className="sr-only">덕질 여권 · {TAB_LABEL[pane]}</h1>
      <div className="om-passport__pad">
        <div className="om-cover">
          <div className="om-cover__who">
            <span className="om-cover__avatar" aria-hidden="true">🐧</span>
            <div>
              <b>{p.displayName}</b>
              <span className="om-cover__vis">{p.visibility === "PRIVATE" ? "비공개" : "공개"}</span>
            </div>
          </div>
          <button type="button" className="om-cover__share" onClick={() => setSharing(true)}
            aria-label="공유 카드 만들기">공유</button>
          <div className="om-cover__stats">
            <Stat n={p.totalPlaces} label="방문 장소" />
            <Stat n={heldStamps.length} label="스탬프" />
            <Stat n={p.cities.length} label="지역" />
          </div>
        </div>

        <div className="om-segtabs" role="tablist" aria-label="여권 보기">
          {PASSPORT_SECTIONS.map((k) => (
            <button key={k} role="tab" aria-selected={pane === k}
              onClick={() => navigate(`${ROUTES.passport}/${k}`)}>{TAB_LABEL[k]}</button>
          ))}
        </div>
      </div>

      {sharing ? <ShareSheet onClose={() => setSharing(false)} /> : null}

      {pane === "stamps" ? (
        isEmpty ? (
          <div className="om-passport__pad">
            <div className="om-emptypass">
              <div className="om-stampgrid" aria-hidden="true">
                {[0, 1, 2].map((i) => <div key={i} className="om-stamp om-stamp--locked">?</div>)}
              </div>
              <h2 className="om-emptypass__title">
                첫 스탬프까지 <span style={{ color: "var(--emotion-oshi-text)" }}>1곳</span>
              </h2>
              <p className="om-emptypass__body">한 곳만 다녀와서 기록하면 여권이 열립니다.</p>
              <Button variant="primary" onClick={() => navigate(ROUTES.map)}>주변 둘러보기</Button>
            </div>
          </div>
        ) : (
          <div className="om-passport__pad">
            <h2 className="om-section__title" style={{ padding: 0 }}>
              스탬프 <span className="num om-count">{heldStamps.length}</span>
            </h2>
            <div className="om-stampgrid">
              {heldStamps.map((s) => {
                const rot = stableRotation(s.id, ROTATION_MAX.stamp);
                const open = flipped === s.id;
                /*
                 * FLIP (15 §3.6) — 스탬프를 뒤집으면 언제 왜 받았는지 나온다.
                 * 앞면에 다 적으면 스탬프가 아니라 목록이 된다.
                 */
                return (
                  <button key={s.id} type="button" className="om-stamp" aria-expanded={open}
                    aria-label={`${s.name} 스탬프${open ? " 접기" : " 자세히"}`}
                    style={{ transform: `rotate(${rot.toFixed(2)}deg)` }}
                    onClick={(e) => { flip(e.currentTarget); setFlipped(open ? null : s.id); }}>
                    {open ? (
                      <>
                        <span className="om-stamp__desc">{s.description || s.name}</span>
                        <span className="om-stamp__when num">{formatDay(s.awardedAt)}</span>
                      </>
                    ) : (
                      <>
                        <span className="om-stamp__label">{s.name}</span>
                        <span className="om-stamp__when num">{formatDay(s.awardedAt).slice(0, 7)}</span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )
      ) : pane === "regions" ? (
        <div className="om-passport__pad">
          <h2 className="om-section__title" style={{ padding: 0 }}>지역별</h2>
          {p.cities.length === 0 ? (
            <EmptyState level={3} title="아직 다녀온 지역이 없어요" body="방문을 기록하면 지역별로 모입니다." />
          ) : (
            <ul className="om-rowlist">
              {p.cities.map((c) => (
                /*
                 * 실측: 서버의 `cities[].count` 는 **방문 횟수**다
                 * (`Counter(region_for(...) for item in visible)`), 장소 수가 아니다.
                 * "곳" 이라고 쓰면 표지의 "방문 장소 4" 와 같은 화면에서 어긋난다 —
                 * 같은 곳을 두 번 가면 4곳인데 5곳이라고 적힌다.
                 */
                <li key={c.name}><span>{c.name}</span><b className="num">{c.count}번</b></li>
              ))}
            </ul>
          )}

          {/*
            * 20개만 보여주되 **잘랐다는 사실을 밝힌다.** 내 기록이 말없이 사라지면
            * 기록한 것이 남지 않았다고 오해한다.
            */}
          <h2 className="om-section__title" style={{ padding: 0, marginTop: "var(--s-6)" }}>
            방문 타임라인
            {p.timeline.length > TIMELINE_MAX
              ? <span className="om-count num"> 최근 {TIMELINE_MAX} / {p.timeline.length}</span>
              : p.timeline.length > 0 ? <span className="om-count num"> {p.timeline.length}</span> : null}
          </h2>
          {p.timeline.length === 0 ? (
            <p className="om-note">기록이 쌓이면 여기에 시간순으로 보입니다.</p>
          ) : (
            <ul className="om-rowlist">
              {p.timeline.slice(0, TIMELINE_MAX).map((v) => (
                <li key={v.id}>
                  <span>
                    <b>{v.place?.name ?? v.event?.name ?? "기록"}</b>
                    {v.memo ? <small>{v.memo}</small> : null}
                  </span>
                  <b className="num">{v.visitedOn.slice(5).replace("-", ".")}</b>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <WorldView passport={p} />
      )}
    </div>
  );
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <div className="om-cover__stat">
      <b className="num">{n}</b>
      <span>{label}</span>
    </div>
  );
}
